(() => {
  const root = document.getElementById('VunotiWelcomePopup');
  if (!root || root.dataset.initialized === 'true') return;

  root.dataset.initialized = 'true';

  const dialog = root.querySelector('.vunoti-welcome__dialog');
  const form = root.querySelector('.vunoti-welcome__form');
  const status = root.querySelector('.vunoti-welcome__form-status');
  const closeButtons = root.querySelectorAll('[data-welcome-close]');
  const successPanel = root.querySelector('[data-welcome-success-panel]');
  const formPanel = root.querySelector('[data-welcome-form-panel]');
  const successKicker = root.querySelector('[data-welcome-success-kicker]');
  const successCopy = root.querySelector('[data-welcome-success-copy]');
  const shopLink = root.querySelector('.vunoti-welcome__shop');
  const delay = Number(root.dataset.delay) || 10000;
  const keys = {
    activeTime: 'vunotiWelcomeActiveTime',
    shown: 'vunotiWelcomeShown',
    pending: 'vunotiWelcomePending',
    subscribed: 'vunotiWelcomeSubscribed',
  };

  let previousFocus = null;
  let activeStartedAt = document.hidden ? null : Date.now();
  let activeTime = Number(sessionStorage.getItem(keys.activeTime)) || 0;
  let timer = null;
  let isOpen = false;
  let handlersAttached = false;

  document.body.classList.remove('vunoti-welcome-open');

  const formSucceeded = status?.dataset.welcomeSuccess === 'true';
  const formErrored = status?.dataset.welcomeError === 'true';
  const explicitAlreadySubscribed = status?.dataset.welcomeAlreadySubscribed === 'true';
  const returnedEmail = status?.dataset.welcomeReturnedEmail === 'true';
  const successMarker = new URLSearchParams(window.location.search).get('welcome_signup') === 'success';
  const pendingSubmission = sessionStorage.getItem(keys.pending) === 'true';
  const alreadySubscribed =
    explicitAlreadySubscribed ||
    (successMarker && pendingSubmission && !formSucceeded && !formErrored && returnedEmail);
  const processedSubmission =
    formSucceeded || alreadySubscribed || (successMarker && pendingSubmission && !formErrored);

  function clearSubmissionMarker() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('welcome_signup') && url.hash !== '#VunotiWelcomePopup') return;

    url.searchParams.delete('welcome_signup');
    if (url.hash === '#VunotiWelcomePopup') url.hash = '';
    window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  }

  function persistActiveTime() {
    if (activeStartedAt !== null) {
      activeTime += Date.now() - activeStartedAt;
      activeStartedAt = Date.now();
    }
    sessionStorage.setItem(keys.activeTime, String(activeTime));
  }

  function getFocusableElements() {
    return Array.from(
      dialog.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hidden && element.offsetParent !== null);
  }

  function openPopup({ recordShown = true } = {}) {
    if (isOpen) return;
    if (!root.isConnected) {
      releaseScrollLock();
      return;
    }

    isOpen = true;
    previousFocus = document.activeElement;
    root.hidden = false;
    root.setAttribute('aria-hidden', 'false');
    document.body.classList.add('vunoti-welcome-open');

    if (recordShown) sessionStorage.setItem(keys.shown, 'true');
    window.requestAnimationFrame(() => root.classList.add('is-open'));
    window.setTimeout(() => {
      const focusTarget = root.querySelector('.vunoti-welcome__success') || root.querySelector('input') || dialog;
      focusTarget.focus();
    }, 260);
  }

  function releaseScrollLock() {
    document.body.classList.remove('vunoti-welcome-open');
  }

  function hidePopupImmediately({ recordShown = isOpen } = {}) {
    window.clearInterval(timer);
    timer = null;
    if (recordShown) sessionStorage.setItem(keys.shown, 'true');

    isOpen = false;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    root.hidden = true;
    releaseScrollLock();
  }

  function closePopup() {
    if (!isOpen) {
      releaseScrollLock();
      return;
    }

    isOpen = false;
    window.clearInterval(timer);
    timer = null;
    sessionStorage.setItem(keys.shown, 'true');
    clearSubmissionMarker();
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    releaseScrollLock();

    window.setTimeout(() => {
      root.hidden = true;
      if (previousFocus instanceof HTMLElement) previousFocus.focus();
    }, 240);
  }

  function maybeOpen() {
    if (!root.isConnected) {
      window.clearInterval(timer);
      timer = null;
      releaseScrollLock();
      return;
    }

    if (isOpen) return;

    if (
      localStorage.getItem(keys.subscribed) === 'true' ||
      sessionStorage.getItem(keys.shown) === 'true'
    ) {
      window.clearInterval(timer);
      timer = null;
      releaseScrollLock();
      return;
    }

    persistActiveTime();
    if (activeTime < delay) return;

    window.clearInterval(timer);
    timer = null;
    openPopup();
  }

  function handleVisibilityChange() {
    if (!root.isConnected) {
      releaseScrollLock();
      activeStartedAt = null;
      return;
    }

    if (document.hidden) {
      persistActiveTime();
      activeStartedAt = null;
      return;
    }

    activeStartedAt = Date.now();
    maybeOpen();
  }

  function handlePageHide() {
    persistActiveTime();
    hidePopupImmediately({ recordShown: isOpen });
  }

  function handlePageShow(event) {
    if (!event.persisted) return;

    hidePopupImmediately({ recordShown: sessionStorage.getItem(keys.shown) === 'true' });
    clearSubmissionMarker();

    if (
      localStorage.getItem(keys.subscribed) === 'true' ||
      sessionStorage.getItem(keys.shown) === 'true'
    ) {
      return;
    }

    activeStartedAt = document.hidden ? null : Date.now();
    timer = window.setInterval(maybeOpen, 500);
    maybeOpen();
  }

  function handleKeydown(event) {
    if (!isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closePopup();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = getFocusableElements();
    if (!focusable.length) {
      event.preventDefault();
      dialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function handleSubmit() {
    persistActiveTime();
    sessionStorage.setItem(keys.pending, 'true');
  }

  function attachHandlers() {
    if (handlersAttached) return;

    handlersAttached = true;
    closeButtons.forEach((button) => button.addEventListener('click', closePopup));
    shopLink?.addEventListener('click', () => hidePopupImmediately({ recordShown: true }));
    form?.addEventListener('submit', handleSubmit);
    document.addEventListener('keydown', handleKeydown);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
  }

  if (alreadySubscribed) {
    if (successKicker) successKicker.textContent = 'Already subscribed';
    if (successCopy) successCopy.textContent = 'You are already part of the VUNOTI community.';
  }

  if (processedSubmission) {
    attachHandlers();
    if (successPanel) successPanel.hidden = false;
    if (formPanel) formPanel.hidden = true;
    localStorage.setItem(keys.subscribed, 'true');
    sessionStorage.removeItem(keys.pending);
    clearSubmissionMarker();
    openPopup({ recordShown: true });
    return;
  }

  if (formErrored && pendingSubmission) {
    attachHandlers();
    openPopup({ recordShown: false });
    return;
  }

  if (localStorage.getItem(keys.subscribed) === 'true') {
    clearSubmissionMarker();
    root.remove();
    return;
  }

  if (sessionStorage.getItem(keys.shown) === 'true') {
    clearSubmissionMarker();
    root.remove();
    return;
  }

  clearSubmissionMarker();

  attachHandlers();
  timer = window.setInterval(maybeOpen, 500);
  maybeOpen();
})();
